// Angular imports
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';

// 3rd party imports
import { Subscription } from 'rxjs';

// devextreme imports
import { formatNumber } from 'devextreme/localization';

// CC-Common Imports
import { IconConstants, Utils } from 'cc-common';

// Service imports
import { ScreenService } from '../../../_services/shared-services/screen.service';
import { PrecisionService } from '../../../_services/shared-services/precision.service';
import { ServerErrorService } from '../../../_services/shared-services/server-error.service';
import { CBBatchDetailService } from '../_services/cb-batch-detail.service';
import { DetailPopupsService } from '../_services/detail-popups.service';
import { HelperService } from '../../../_services/shared-services/helper.service';

// Constant imports
import { StringFormatConstants } from '../../../_constants/string-format-constants';

// Model imports
import { BatchDetailResultDto } from '../_models/cb-batches/batch-detail-result-dto';
import { BatchParametersDto } from '../_models/cb-batches/batch-parameters-dto';
import { BatchItemResultDto } from '../_models/cb-batches/batch-item-result-dto';

@Component({
    selector: 'app-cb-batch-details',
    templateUrl: './cb-batch-details.component.html',
    styleUrls: ['./cb-batch-details.component.scss']
})
export class CBBatchDetailsComponent implements OnInit, OnDestroy {
    @Input() visible: boolean;
    @Input() batchDetailParam: BatchParametersDto;
    @Output() onBatchDetailClose = new EventEmitter<boolean>();

    loadingVisible = false;
    isSmallScreen = false;
    popupTitle: string;
    popupWidth = 'auto';
    popupHeight = 'auto';

    dateFormat = StringFormatConstants.dateFormat;
    currencyFormat = StringFormatConstants.currencyFormat;

    batchDetailResult: BatchDetailResultDto;

    iconClose = IconConstants.CROSS_MARK;
    forceReloadSubscription: Subscription;

    constructor(
        private readonly screenService: ScreenService,
        private readonly precisionService: PrecisionService,
        private readonly errorService: ServerErrorService,
        private readonly batchService: CBBatchDetailService,
        private readonly detailPopupsService: DetailPopupsService,
        private readonly helperService: HelperService
    ) {
        this.currencyFormat = this.precisionService.getCurrencyFormat();
        this.calculateCustomSummary = this.calculateCustomSummary.bind(this);
    }

    ngOnInit(): void {
        this.updateScreenSize();
        this.screenService.changed.subscribe(this.updateScreenSize.bind(this));
        if (!!this.batchDetailParam) {
            this.popupTitle = `Batch Details# ${this.batchDetailParam.batchID}`;
            this.populateBatchDetails();
        }

        this.forceReloadSubscription = this.detailPopupsService.onForceReload().subscribe(() => {
            this.populateBatchDetails();
        });
    }

    ngOnDestroy() {
        if (Utils.notNullAndDefined(this.forceReloadSubscription)) {
            this.forceReloadSubscription.unsubscribe();
        }
    }

    onShown(e): void {
        e.component.repaint();
    }

    onHidden(): void {
        this.visible = false;
        this.onBatchDetailClose.emit();
    }

    currencyFormatter(value: number): string {
        return formatNumber(value, this.currencyFormat);
    }

    hasHyperlink(rec: BatchItemResultDto): boolean {
        return this.detailPopupsService.hasDetailsPopup(this.batchDetailResult.type, rec.sourceType);
    }

    onAmountClick(rec: BatchItemResultDto): void {
        this.detailPopupsService.showDetailsPopup(this.batchDetailResult.type, rec.sourceType, {
            managementCompanyId: this.batchDetailResult.managementCompanyID,
            communityId: this.batchDetailResult.communityID,
            headerId: rec.sourceHeaderID,
            vendorCode: rec.vendorCustomerCode
        });
    }

    onVendorCustomerClick(rec: BatchItemResultDto): void {
        if (this.batchDetailResult.type === 'AR') {
            this.navigateToCustomerPage(rec.vendorCustomerCode, this.batchDetailResult.communityID);
        }
    }

    calculateCustomSummary(e): void {
        this.helperService.calculateTotalSummary(e);
    }

    private populateBatchDetails(): void {
        this.loadingVisible = true;
        this.batchService.getBatchDetail(this.batchDetailParam).subscribe(
            (result) => {
                result.items.sort((a, b) => {
                    if (a.postDate > b.postDate) {
                        return -1;
                    } else if (a.postDate < b.postDate) {
                        return 1;
                    }
                    return 0;
                });
                this.batchDetailResult = result;
                this.loadingVisible = false;
                this.visible = true;
            },
            (error) => {
                this.loadingVisible = false;
                this.errorService.displayError(error);
            }
        );
    }

    private navigateToCustomerPage(customerId: string, communityId: number): void {
        const urlSegments = ['property', communityId.toString(), customerId, 'home'];
        const urlPath = urlSegments.join('/');
        window.open(`${urlPath.startsWith('/') ? urlPath.replace('/', '') : urlPath}`, '_blank');
    }

    private updateScreenSize(): void {
        this.isSmallScreen = this.screenService.isSmallScreen();
        if (this.isSmallScreen) {
            this.popupWidth = '95%';
            this.popupHeight = '90%';
        } else {
            this.popupWidth = 'auto';
            this.popupHeight = 'auto';
        }
    }
}
